import type {
  GamePlayData,
  GameSessionPageData,
  InstantWinDraw,
  InstantWinNonWinnerEntries,
  InstantWinPrize,
  InstantWinWinnerEntries,
} from '@/types'
import { DateTime } from 'luxon'

type GameSessionOptions = {
  /** The probability of winning a prize. Set to `0` to always lose, `1` to
   * always. Defaults to `0.5`, which gives a 50/50 chance.
   */
  winProbability?: number
  /**
   * The number of plays already completed. Use this to sumulate a partially
   * played game. Defaults to `0`, which is a new, unplayed game.
   */
  completedPlays?: number
  /** A custom SessionID */
  sessionId?: string
  /**
   * Optional text prefix for a ticket. Setting `TEST` would create a ticket
   * of `TEST-1234`. Leaving this blank will result in an unprefixed ticket
   */
  ticketPrefix?: string
  /**
   * Optional ordered list of prize codes. If set, winning prizes will be
   * returned in this order.
   */
  prizeOrder?: number[]
}

type GameSessionResolvedOptions = {
  winProbability: number
  completedPlays: number
  sessionId: string
  ticketPrefix: string
  prizeOrder: number[]
}

type DemoSession = {
  game: InstantWinDraw
  session: GameSessionPageData
  options: GameSessionResolvedOptions
}

const sessions = {} as Record<string, DemoSession>

/**
 * Creates a game session that will return random prizes for a given Instant
 * Win game.
 *
 * @param game The InstantWin game the demo session is for
 * @param plays The number of plays the demo session will create tickets for
 * @param options Optional configuration for the session
 * @returns A game session
 */
const createSession = (
  game: InstantWinDraw,
  plays = 10,
  options: GameSessionOptions = {} as GameSessionOptions
): GameSessionPageData => {
  const now = DateTime.now()
  const resolvedOptions: GameSessionResolvedOptions = {
    winProbability: 0.5,
    sessionId: `LOCAL-${now.toSeconds()}`,
    completedPlays: 0,
    ticketPrefix: '',
    prizeOrder: [],
    ...options,
  }
  const session: GameSessionPageData = {
    game_session: {
      can_play: true,
      completed_plays: resolvedOptions.completedPlays,
      draw_code: game.code,
      game_session_id: resolvedOptions.sessionId,
      purchase_id: Math.random(),
      purchased_at: now,
      remaining_plays: plays - resolvedOptions.completedPlays,
      status: '?',
      total_plays: plays,
    },
    played_entries: createPlayedEntries(
      game.prizes,
      resolvedOptions.completedPlays,
      resolvedOptions.winProbability,
      resolvedOptions.ticketPrefix,
      resolvedOptions.prizeOrder
    ),
    allocated_entries: [],
  }
  sessions[resolvedOptions.sessionId] = {
    game,
    session,
    options: resolvedOptions,
  }
  return session
}

/**
 * Creates a set of played game entries for an InstantWin game.
 *
 * @param game The game the entires are for
 * @param number The number of tickets to play
 * @param winProbability The win probabilty for the game
 * @param ticketPrefix The text prefix for generated tickets
 */
const createPlayedEntries = (
  prizes: InstantWinPrize[],
  number: number,
  winProbability: number,
  ticketPrefix: string,
  prizeOrder: number[]
) => {
  const winning_entries = []
  const non_winning_entries = []
  for (let c = 0; c < number; c++) {
    if (Math.random() <= winProbability) {
      let prize: InstantWinPrize

      // If session has a `prizeOrder`, use that to pick the next prize.
      if (prizeOrder.length) {
        // Take the first prize code in the array
        const prizeCode = prizeOrder.shift()
        if (prizeCode) {
          // push the picked prize code back to the end of the array
          prizeOrder.push(prizeCode)
          const prizeCandidate = prizes.find(
            (prize) => prize.prizes_code === prizeCode
          )
          if (prizeCandidate) {
            prize = prizeCandidate
          }
        }
      }
      // If we don't have a prize yet, pick one at random
      prize ??= prizes.at(Math.random() * prizes.length)!

      let prizeValue = prize.value

      // Credit win values aren't stored in the JSON files so we need to parse
      // the text of the prize to determine their value
      if (prize.prize_type === 'credit') {
        const match = /(£[.\d]+|\b\d+p)\b/.exec(prize.title)
        if (match) {
          const [textValue] = match
          if (textValue.startsWith('£')) {
            prizeValue = parseFloat(textValue.slice(1))
          } else {
            prizeValue = parseFloat(textValue.slice(0, -1)) / 100
          }
        }
      }
      const entry: InstantWinWinnerEntries = {
        entry_code: generateTicketNumber(ticketPrefix),
        prize_code: prize.prizes_code,
        prize_name: prize.title,
        image: prize.image,
        prize_quantity: 1,
        prize_type: prize.prize_type,
        prize_value: prizeValue * 100,
      }
      winning_entries.push(entry)
    } else {
      const entry: InstantWinNonWinnerEntries = {
        entry_code: generateTicketNumber(ticketPrefix),
      }
      non_winning_entries.push(entry)
    }
  }
  return {
    winning_entries,
    non_winning_entries,
  }
}

/**
 * Generates a random ticket number
 * @param prefix Optional prefix to prepend to the ticket number
 */
const generateTicketNumber = (prefix?: string): string => {
  const number = (Math.random() * 99999).toFixed(0)
  if (prefix) {
    return `${prefix}-${number}`
  }
  return number
}

/**
 * Returns the current demo game session
 */
const getSession = (gameSessionId: string): GameSessionPageData => {
  if (!sessions[gameSessionId]) {
    throw new Error('Failed to get game session')
  }
  return sessions[gameSessionId].session
}

/**
 * Plays the currently configured game the specified number of times
 *
 * @param gameSessionId
 * @param playCount
 */
const playGame = (
  gameSessionId: string,
  playCount: number = 1
): GamePlayData => {
  if (!sessions[gameSessionId]) {
    throw new Error('Failed to play game session')
  }
  const { session, game, options } = sessions[gameSessionId]

  const response: GamePlayData = {
    played_count: 0,
    played_entries: {
      winning_entries: [],
      non_winning_entries: [],
    },
    remaining_plays: 0,
    completed_plays: 0,
    game_complete: false,
    session_status: '?',
  }

  const entriesToCreate = Math.min(
    playCount,
    session.game_session.remaining_plays
  )
  const playedEntries = createPlayedEntries(
    game.prizes,
    entriesToCreate,
    options.winProbability,
    options.ticketPrefix,
    options.prizeOrder
  )

  session.game_session.completed_plays += entriesToCreate
  session.game_session.remaining_plays -= entriesToCreate
  session.played_entries?.winning_entries.push(...playedEntries.winning_entries)
  session.played_entries?.non_winning_entries.push(
    ...playedEntries.non_winning_entries
  )

  response.played_count = entriesToCreate
  response.played_entries = playedEntries
  response.remaining_plays = session.game_session.remaining_plays
  response.completed_plays = session.game_session.completed_plays
  response.game_complete = response.remaining_plays === 0

  return response
}

/**
 * Deletes an existing game session
 * @param gameSessionId
 */
const deleteSession = (gameSessionId: string) => {
  delete sessions[gameSessionId]
}
export const GameLocalSession = {
  createSession,
  getSession,
  deleteSession,
  playGame,
}
